import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";

import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  FileText,
  Users,
  Settings,
  Rocket,
} from "lucide-react";

import Step1TemplateSelection from "./Step1TemplateSelection";
import Step2QuestionPreview from "./Step2QuestionPreview";
import Step3AccountTargeting from "./Step3AccountTargeting";
import Step4Configuration from "./Step4Configuration";
import Step5ReviewLaunch from "./Step5ReviewLaunch";
import {
  AssessmentCreationWizardProps,
  FormData,
  Account,
  AccountTeamsBatchEntry,
  AccountTeamsByAccountId,
  Executive,
} from "./types";
import Button from "../../../../Components/Button";
import Modal from "../../../../Components/Modal";
import { useToast } from "../../../../Components/Toast";
import { useAppSelector, useAppDispatch } from "../../../../Store/Hooks";
import { fetchOrganizationLevels } from "../../../../Store/Reducers/AdminSlice";
import {
  checkFormalRunning,
  fetchAssessmentTemplates,
  fetchAccountUsers,
  fetchAssessmentAccountsTeamsBatch,
  clearAccountTeamsBatch,
  createAssessment,
  updateAssessment,
  updateAssessmentStatus,
  type CreateAssessmentRejection,
} from "../../../../Store/Reducers/AssessmentSlice";
import { fetchDisconnectFlowConfig } from "../../../../Store/Reducers/VendorAdminSlice";
import { toUtcDateInputValue } from "../../../../Utils/Helper/assessmentDateUtils";
import { buildDefaultFormalDueOn } from "../../../../Utils/Helper/assessmentDueDefaults";
import { getAccountLevelCompactLabel } from "../../../../Utils/Helper/levelUtils";
import { filterValidUUIDs } from "../../../../Utils/Helper/uuidValidator";

const isSelfAssessmentTemplate = (template: {
  json_schema?: {
    metadata?: { template_kind?: string; categories?: string[] };
  };
  metadata?: { template_kind?: string; categories?: string[] };
  categories?: string[];
}): boolean => {
  const metadata = template.json_schema?.metadata ?? template.metadata ?? {};
  if (metadata.template_kind === "self_assessment") return true;
  const categories = metadata.categories ?? template.categories ?? [];
  return Array.isArray(categories) && categories.includes("self_assessment");
};

const mapAssignedSponsor = (
  accountId: string,
  role: string,
  person: { id?: string; name?: string; title?: string; email?: string },
  idOverride?: string
): Executive | null => {
  const id = idOverride || person.id;
  if (!id) return null;

  return {
    id,
    name: person.name ?? "Executive",
    title: person.title ?? "",
    email: person.email ?? "",
    role,
    accountId,
  };
};

const mapAccountTeamsBatch = (
  batch: Record<string, any>
): AccountTeamsByAccountId => {
  const mapped: AccountTeamsByAccountId = {};

  Object.entries(batch).forEach(([accountId, entry]) => {
    const account = entry?.account ?? {};
    const assignedSponsors: Executive[] = [];

    if (account.primaryExecutive) {
      const sponsor = mapAssignedSponsor(
        accountId,
        "Primary Executive Sponsor",
        account.primaryExecutive,
        account.primary_executive_id
      );
      if (sponsor) assignedSponsors.push(sponsor);
    }

    if (account.secondaryExecutive) {
      const sponsor = mapAssignedSponsor(
        accountId,
        "Secondary Executive Sponsor",
        account.secondaryExecutive,
        account.secondary_executive_id
      );
      if (sponsor) assignedSponsors.push(sponsor);
    }

    mapped[accountId] = {
      assignedSponsors,
      accountTeam: (entry?.accountTeam ?? [])
        .filter((member: any) => member.id)
        .map((member: any) => ({
          id: member.id,
          name: member.member_name,
          title: member.member_title ?? "",
          role: member.role ?? "Account Team",
          email: member.member_email,
        })),
      sponsors: (entry?.sponsors ?? [])
        .filter((sponsor: any) => sponsor.executive_id)
        .map((sponsor: any) => ({
          id: sponsor.executive_id,
          name: sponsor.name,
          role: sponsor.role ?? "executive_sponsor",
          email: sponsor.email,
        })),
    };
  });

  return mapped;
};

const getAllDefaultStakeholderIds = (
  accountId: string,
  teams: AccountTeamsByAccountId,
  accounts: Account[]
): string[] => {
  const entry = teams[accountId];
  if (entry) {
    const ids = [
      ...entry.assignedSponsors.map(sponsor => sponsor.id),
      ...entry.accountTeam.map(member => member.id),
      ...entry.sponsors.map(sponsor => sponsor.id),
    ].filter(Boolean);

    if (ids.length > 0) {
      return ids;
    }
  }

  const account = accounts.find(item => item.id === accountId);
  return account?.executives.map(executive => executive.id) ?? [];
};

const accountHasStakeholders = (
  account: Account,
  teams?: AccountTeamsBatchEntry
): boolean => {
  const assignedSponsors = teams?.assignedSponsors?.length
    ? teams.assignedSponsors
    : (account.executives ?? []);
  const accountTeam = teams?.accountTeam ?? [];
  const otherExecutives = teams?.sponsors ?? [];

  return (
    assignedSponsors.length > 0 ||
    accountTeam.length > 0 ||
    otherExecutives.length > 0
  );
};

const AssessmentCreationWizard: React.FC<AssessmentCreationWizardProps> = ({
  onClose,
  onComplete,
  editingAssessment,
}) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { organizationLevels } = useAppSelector((state: any) => state.admin);
  const { templates: reduxTemplates, isLoading: templatesLoading } =
    useAppSelector(state => state.assessment);
  const {
    accountUsers: reduxAccountUsers,
    accountTeamsBatch,
    isLoadingAccountTeams,
    accountTeamsError,
  } = useAppSelector(state => state.assessment);
  const disconnectFlowValue = useAppSelector(
    state => state.vendorAdmin.disconnectFlowValue
  );

  const accountTeamsByAccountId = useMemo(
    () => mapAccountTeamsBatch(accountTeamsBatch),
    [accountTeamsBatch]
  );

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [templateTab, setTemplateTab] = useState("izba_provided");
  const [formData, setFormData] = useState<FormData>({
    title: "",
    description: "",
    template_id: "",
    template: null,
    due_date: "",
    target_accounts: [],
    account_executives: {},
    questions: [],
    custom_questions: [],
  });
  const [changeTemplateConfirm, setChangeTemplateConfirm] = useState<{
    isOpen: boolean;
    pendingTemplate: any | null;
  }>({ isOpen: false, pendingTemplate: null });
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [formalRunningConflict, setFormalRunningConflict] = useState<
    CreateAssessmentRejection["accounts"] | null
  >(null);
  const [isCheckingLaunchConflicts, setIsCheckingLaunchConflicts] =
    useState(false);
  const isOngoingEdit = useMemo(() => {
    const status = editingAssessment?.status?.toLowerCase();
    return status === "active" || status === "ongoing";
  }, [editingAssessment?.status]);

  // Simple: track if user has edited anything (set true in user handlers only)
  const hasUserEditedRef = useRef(false);

  // Reset "user edited" when opening wizard or switching assessment
  useEffect(() => {
    hasUserEditedRef.current = false;
  }, [editingAssessment?.id]);

  // Prefill Formal due from org AL Assessment Settings (new Formal only).
  useEffect(() => {
    dispatch(fetchDisconnectFlowConfig());
  }, [dispatch]);

  useEffect(() => {
    if (editingAssessment) return;
    const nextDue = buildDefaultFormalDueOn(
      disconnectFlowValue?.formal_default_due_date
    );
    setFormData(prev => {
      if (hasUserEditedRef.current) return prev;
      if (prev.due_date === nextDue) return prev;
      return { ...prev, due_date: nextDue };
    });
  }, [editingAssessment, disconnectFlowValue?.formal_default_due_date]);

  const steps = [
    { id: 1, name: "Template Selection", icon: FileText },
    { id: 2, name: "Question Preview", icon: FileText },
    { id: 3, name: "Configuration", icon: Settings },
    { id: 4, name: "Account Targeting", icon: Users },
    { id: 5, name: "Review & Launch", icon: Rocket },
  ];

  // Helper function to find account ID in the accounts list
  const findAccountId = useCallback(
    (assessmentAccountId: string, accountsList: Account[]): string | null => {
      if (!assessmentAccountId || accountsList.length === 0) return null;

      const matchedAccount = accountsList.find(acc => {
        const accAny = acc as any;
        return (
          acc.id === assessmentAccountId ||
          accAny.customer_id === assessmentAccountId ||
          accAny.account_id === assessmentAccountId
        );
      });

      return matchedAccount ? matchedAccount.id : null;
    },
    []
  );

  // Helper function to match executives
  const matchExecutives = useCallback(
    (
      assessmentExecutives: any[],
      accountExecutives: Account["executives"]
    ): string[] => {
      if (!accountExecutives || accountExecutives.length === 0) return [];

      const matchedIds: string[] = [];
      assessmentExecutives.forEach((assessmentExec: any) => {
        const execEmail = assessmentExec.email || "";
        const execUserId = assessmentExec.id || assessmentExec.user_id || "";

        const matchedExec = accountExecutives.find(
          exec =>
            exec.email === execEmail ||
            exec.id === execUserId ||
            (exec as any).user_id === execUserId
        );

        if (matchedExec) {
          matchedIds.push(matchedExec.id);
        }
      });

      return matchedIds.length > 0
        ? matchedIds
        : accountExecutives.map(exec => exec.id);
    },
    []
  );

  // Format date helper (UTC calendar day for date inputs)
  const formatDateForInput = useCallback(
    (dateString: string | undefined): string => toUtcDateInputValue(dateString),
    []
  );

  // Transform API question type to wizard format
  const mapApiQuestionTypeToWizard = useCallback((apiType: string): string => {
    switch (apiType) {
      case "free_text":
        return "textarea";
      case "single_choice":
        return "dropdown";
      case "multiple_choice":
        return "multiple_choice";
      case "scale":
        return "scale";
      default:
        return "textarea";
    }
  }, []);

  // Transform API question options to wizard format
  const transformApiOptionsToWizard = useCallback(
    (options: any): any[] | null => {
      if (!options) return null;

      // Handle choices (for dropdown/multiple_choice)
      if (options.choices && Array.isArray(options.choices)) {
        return options.choices.map((choice: any) => ({
          label: choice.label || choice.value || "",
          value: choice.value || choice.label || "",
        }));
      }

      // Handle scale with min/max
      if (options.min !== undefined || options.max !== undefined) {
        const min = options.min || 1;
        const max = options.max || 10;
        const labels = options.labels || [];
        return [
          { label: `Min: ${min}`, value: `min:${min}`, min },
          { label: `Max: ${max}`, value: `max:${max}`, max },
          ...labels.map((label: string, idx: number) => ({
            label,
            value: `label:${idx}`,
          })),
        ];
      }

      // Handle textarea options (min_length/max_length)
      if (
        options.min_length !== undefined ||
        options.max_length !== undefined
      ) {
        return null; // Textarea doesn't need options
      }

      return null;
    },
    []
  );

  // Extract and transform custom questions from API format
  const extractCustomQuestionsFromAssessment = useCallback(
    (assessment: NonNullable<typeof editingAssessment>): any[] => {
      const questionsData = assessment.questions_data?.questions || [];

      if (!Array.isArray(questionsData) || questionsData.length === 0) {
        return [];
      }

      // Filter custom questions (is_custom: true)
      const customQuestions = questionsData.filter(
        (q: any) => q.is_custom === true
      );

      // Transform to wizard format and sort by order_index
      return customQuestions
        .map((apiQuestion: any) => {
          const wizardQuestion: any = {
            id: apiQuestion.id || `custom-${Date.now()}-${Math.random()}`,
            question: apiQuestion.question_text || "",
            type: mapApiQuestionTypeToWizard(
              apiQuestion.question_type || "free_text"
            ),
            required:
              apiQuestion.required !== undefined ? apiQuestion.required : false,
            order_index: apiQuestion.order_index,
          };

          // Transform options
          const transformedOptions = transformApiOptionsToWizard(
            apiQuestion.options
          );
          wizardQuestion.options = transformedOptions;

          return wizardQuestion;
        })
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    },
    [mapApiQuestionTypeToWizard, transformApiOptionsToWizard]
  );

  // Extract and match accounts from assessment
  const extractAccountsFromAssessment = useCallback(
    (
      assessment: NonNullable<typeof editingAssessment>
    ): {
      targetAccountIds: string[];
      accountExecutives: Record<string, string[]>;
    } => {
      const targetAccountIds: string[] = [];
      const accountExecutives: Record<string, string[]> = {};

      // Process accounts array
      if (assessment.accounts && assessment.accounts.length > 0) {
        assessment.accounts.forEach(account => {
          const assessmentAccountId = account.id || account.account_id || "";
          if (!assessmentAccountId) return;

          const matchedAccountId = findAccountId(assessmentAccountId, accounts);
          if (!matchedAccountId) return;

          targetAccountIds.push(matchedAccountId);
          const matchedAccount = accounts.find(
            acc => acc.id === matchedAccountId
          );

          if (matchedAccount?.executives) {
            const assessmentExecutives =
              account.executives || account.users || [];
            accountExecutives[matchedAccountId] = matchExecutives(
              assessmentExecutives,
              matchedAccount.executives
            );
          }
        });
      }
      // Process target_accounts array
      else if (
        assessment.target_accounts &&
        assessment.target_accounts.length > 0
      ) {
        assessment.target_accounts.forEach(acc => {
          if (!acc.account_id) return;

          const matchedAccountId = findAccountId(acc.account_id, accounts);
          if (!matchedAccountId) return;

          targetAccountIds.push(matchedAccountId);
          const matchedAccount = accounts.find(a => a.id === matchedAccountId);

          if (matchedAccount?.executives) {
            accountExecutives[matchedAccountId] = matchedAccount.executives.map(
              exec => exec.id
            );
          }
        });
      }

      return { targetAccountIds, accountExecutives };
    },
    [accounts, findAccountId, matchExecutives]
  );

  const lockedTargetAccountIds = useMemo(() => {
    if (!isOngoingEdit || !editingAssessment || accounts.length === 0) {
      return [];
    }

    const rawAccountIds = [
      ...(editingAssessment.accounts || []).map(
        account => account.id || account.account_id || ""
      ),
      ...(editingAssessment.target_accounts || []).map(
        account => account.account_id || ""
      ),
    ].filter(Boolean);

    const mappedAccountIds = rawAccountIds
      .map(accountId => findAccountId(accountId, accounts))
      .filter((id): id is string => Boolean(id));

    return Array.from(new Set(mappedAccountIds));
  }, [isOngoingEdit, editingAssessment, accounts, findAccountId]);

  // Initialize form data when editing
  useEffect(() => {
    if (!editingAssessment) return;

    const isNotInitialized = formData.title !== editingAssessment.name;
    const hasAccountData =
      (editingAssessment.accounts?.length ?? 0) > 0 ||
      (editingAssessment.target_accounts?.length ?? 0) > 0;
    const needsAccountInitialization =
      accounts.length > 0 &&
      formData.target_accounts.length === 0 &&
      hasAccountData;

    // Check if custom questions need initialization
    const hasCustomQuestionsData =
      editingAssessment.questions_data?.questions?.some(
        (q: any) => q.is_custom === true
      ) || false;
    const needsCustomQuestionsInitialization =
      formData.custom_questions.length === 0 && hasCustomQuestionsData;

    if (
      !isNotInitialized &&
      !needsAccountInitialization &&
      !needsCustomQuestionsInitialization
    )
      return;

    const formattedDate = formatDateForInput(
      editingAssessment.dueDate || editingAssessment.due_on
    );

    // Extract custom questions
    const customQuestions =
      extractCustomQuestionsFromAssessment(editingAssessment);

    // Initialize accounts if available
    if (accounts.length > 0 && hasAccountData) {
      const { targetAccountIds, accountExecutives } =
        extractAccountsFromAssessment(editingAssessment);

      if (needsAccountInitialization && !isNotInitialized) {
        // Only update account-related fields and custom questions if needed
        setFormData(prev => {
          if (prev.title === editingAssessment.name) {
            return {
              ...prev,
              target_accounts: targetAccountIds,
              account_executives: accountExecutives,
              custom_questions:
                needsCustomQuestionsInitialization && customQuestions.length > 0
                  ? customQuestions
                  : prev.custom_questions,
            };
          }
          return prev;
        });
      } else {
        // Update all fields (initial initialization)
        setFormData(prev => ({
          ...prev,
          title: editingAssessment.name || "",
          description: editingAssessment.description || "",
          due_date: formattedDate,
          template_id: editingAssessment.template_id || "",
          target_accounts: targetAccountIds,
          account_executives: accountExecutives,
          custom_questions:
            customQuestions.length > 0
              ? customQuestions
              : prev.custom_questions,
        }));
      }
    } else if (isNotInitialized) {
      // Set basic fields if accounts haven't loaded yet
      setFormData(prev => ({
        ...prev,
        title: editingAssessment.name || "",
        description: editingAssessment.description || "",
        due_date: formattedDate,
        template_id: editingAssessment.template_id || "",
        custom_questions:
          customQuestions.length > 0 ? customQuestions : prev.custom_questions,
      }));
    } else if (needsCustomQuestionsInitialization) {
      // Only update custom questions if accounts are already initialized
      setFormData(prev => ({
        ...prev,
        custom_questions: customQuestions,
      }));
    }
  }, [
    editingAssessment?.id,
    accounts.length,
    extractAccountsFromAssessment,
    formatDateForInput,
    extractCustomQuestionsFromAssessment,
  ]);

  // Restore current step when editing; ongoing assessments should open directly
  // on Account Targeting for faster assignment updates.
  useEffect(() => {
    if (!editingAssessment) return;
    const editStatus = editingAssessment.status?.toLowerCase();
    if (editStatus === "active" || editStatus === "ongoing") {
      setCurrentStep(4);
      return;
    }
    const savedStep =
      (editingAssessment as any).data?.metadata?.current_step ??
      editingAssessment.questions_data?.metadata?.current_step;
    if (
      typeof savedStep === "number" &&
      savedStep >= 1 &&
      savedStep <= steps.length
    ) {
      setCurrentStep(savedStep);
    }
  }, [editingAssessment?.id]);

  const handleCloseRequest = useCallback(() => {
    // Only show confirm modal when user edited, on step 3+ or editing, and title/due_date exist (so Save as Draft is possible)
    const canSaveDraft = !!formData.title?.trim() && !!formData.due_date;
    const shouldConfirm =
      hasUserEditedRef.current &&
      !isOngoingEdit &&
      (currentStep >= 3 || !!editingAssessment) &&
      canSaveDraft;
    if (shouldConfirm) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [
    onClose,
    currentStep,
    isOngoingEdit,
    editingAssessment,
    formData.title,
    formData.due_date,
  ]);

  const handleDiscardAndClose = useCallback(() => {
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const canSaveDraft = !!formData.title?.trim() && !!formData.due_date;
      const shouldWarn =
        hasUserEditedRef.current &&
        !isOngoingEdit &&
        (currentStep >= 3 || !!editingAssessment) &&
        canSaveDraft;
      if (shouldWarn) e.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [
    currentStep,
    editingAssessment,
    isOngoingEdit,
    formData.title,
    formData.due_date,
  ]);

  // Load templates and organization levels (for maturity_level dropdown) from Redux
  useEffect(() => {
    dispatch(fetchAssessmentTemplates({}));
  }, [dispatch]);

  useEffect(() => {
    if (!organizationLevels?.length) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, organizationLevels?.length]);

  const getDefaultExecutiveIdsForAccount = useCallback(
    (accountId: string) =>
      getAllDefaultStakeholderIds(accountId, accountTeamsByAccountId, accounts),
    [accountTeamsByAccountId, accounts]
  );

  // Load accounts
  useEffect(() => {
    dispatch(fetchAccountUsers());
  }, [dispatch]);

  const teamsBatchFetchedRef = useRef(false);

  // Fetch teams-batch once per wizard session when accounts are available
  useEffect(() => {
    if (teamsBatchFetchedRef.current) return;

    const accountIds = filterValidUUIDs(accounts.map(account => account.id));
    if (!accountIds.length) return;

    teamsBatchFetchedRef.current = true;
    dispatch(fetchAssessmentAccountsTeamsBatch({ accountIds }));
  }, [accounts.length, dispatch]);

  useEffect(() => {
    if (accountTeamsError) {
      toast.error(accountTeamsError);
    }
  }, [accountTeamsError, toast]);

  useEffect(() => {
    return () => {
      teamsBatchFetchedRef.current = false;
      dispatch(clearAccountTeamsBatch());
    };
  }, [dispatch]);

  // Transform account users from Redux
  useEffect(() => {
    if (!reduxAccountUsers || reduxAccountUsers.length === 0) {
      setAccounts([]);
      return;
    }

    const accountsWithExecutives: Account[] = reduxAccountUsers.map(
      (account: any) => {
        const executives: any[] = [];

        // Handle new API structure with assigned_users array
        if (account.assigned_users && Array.isArray(account.assigned_users)) {
          account.assigned_users.forEach((assignedUser: any) => {
            const assignmentType = assignedUser.assignment_type || "primary";
            const role =
              assignmentType === "primary"
                ? "Primary Executive Sponsor"
                : "Secondary Executive Sponsor";

            executives.push({
              id: assignedUser.user_id || assignedUser.id || "",
              name: assignedUser.full_name || assignedUser.name || "",
              title: assignedUser.position || assignedUser.title || "",
              email: assignedUser.email || "",
              role: role,
              accountId: account.customer_id || account.id,
            });
          });
        } else {
          // Fallback to old structure for backward compatibility
          if (account.primaryExecutive || account.primary_executive) {
            const primaryExec =
              account.primaryExecutive || account.primary_executive;
            executives.push({
              id:
                primaryExec.user_id ||
                primaryExec.id ||
                `exec-primary-${account.customer_id || account.id}`,
              name:
                primaryExec.full_name ||
                primaryExec.name ||
                "Primary Executive",
              title:
                primaryExec.position ||
                primaryExec.title ||
                "Primary Executive Sponsor",
              email: primaryExec.email || "",
              role: "Primary Executive Sponsor",
              accountId: account.customer_id || account.id,
            });
          }

          if (account.secondaryExecutive || account.secondary_executive) {
            const secondaryExec =
              account.secondaryExecutive || account.secondary_executive;
            executives.push({
              id:
                secondaryExec.user_id ||
                secondaryExec.id ||
                `exec-secondary-${account.customer_id || account.id}`,
              name:
                secondaryExec.full_name ||
                secondaryExec.name ||
                "Secondary Executive",
              title:
                secondaryExec.position ||
                secondaryExec.title ||
                "Secondary Executive Sponsor",
              email: secondaryExec.email || "",
              role: "Secondary Executive Sponsor",
              accountId: account.customer_id || account.id,
            });
          }
        }

        return {
          id: account.customer_id || account.id,
          name: account.name || account.customer_name,
          tier: account.tier || "",
          city: account.city || "",
          country: account.country || "",
          revenue: account.revenue || account.annual_revenue || 0,
          executives: executives,
        };
      }
    );

    setAccounts(accountsWithExecutives);
  }, [reduxAccountUsers]);

  // Transform all templates (before filtering by templateTab)
  const allTemplatesTransformed = useMemo(() => {
    if (!reduxTemplates) {
      return [];
    }

    // Handle different response structures (same as TemplateList)
    const templatesData =
      reduxTemplates?.templates ||
      reduxTemplates?.data?.templates ||
      (Array.isArray(reduxTemplates) ? reduxTemplates : []);

    return (Array.isArray(templatesData) ? templatesData : [])
      .filter((template: any) => !isSelfAssessmentTemplate(template))
      .map((template: any) => {
        // Map question types from API format to component format
        const mapQuestionType = (questionType: string): string => {
          switch (questionType) {
            case "free_text":
              return "textarea";
            case "single_choice":
              return "dropdown";
            case "multiple_choice":
              return "multiple_choice";
            case "scale":
              return "scale";
            default:
              return questionType || "textarea"; // Default to textarea if unknown
          }
        };

        // Determine category from type or questionnaire_type
        const category =
          template.type ||
          template.category ||
          (template.questionnaire_type === "small" ||
          template.questionnaire_type === "short"
            ? "short"
            : "long");

        // Determine template type: if is_global is true, it's Izba template, otherwise it's custom
        const template_type: "izba_provided" | "custom" =
          template.is_global === true ? "izba_provided" : "custom";

        // Build latest maturity_level options from organizationLevels (consume level_code and name as-is)
        const getMaturityLevelOptions = () => {
          if (!organizationLevels?.length) return null;
          return organizationLevels.map((level: any) => ({
            label: getAccountLevelCompactLabel(level.level_code, level.name),
            value: level.level_code,
          }));
        };

        // Transform questions from json_schema format; inject latest options for maturity_level
        const questions = (
          template.json_schema?.questions ||
          template.questions ||
          []
        )
          .map((q: any) => {
            const templateOptions = q.options?.choices
              ? q.options.choices.map((choice: any) => ({
                  value: choice.value,
                  label: choice.label,
                }))
              : q.options || null;
            const isMaturityLevel =
              q.id === "maturity_level" || q.id === "maturity level";
            const options =
              isMaturityLevel && getMaturityLevelOptions()
                ? getMaturityLevelOptions()
                : templateOptions;

            return {
              id: q.id,
              question: q.question_text || q.question || "",
              type: mapQuestionType(
                q.question_type || q.type || q.questionType || "text"
              ),
              required: q.required !== undefined ? q.required : true,
              options,
              order_index: q.order_index || q.display_order || 0,
            };
          })
          .sort(
            (a: any, b: any) => (a.order_index || 0) - (b.order_index || 0)
          );

        return {
          id: template.template_id || template.id,
          name: template.name || "",
          description: template.description || "",
          category: category,
          template_type: template_type,
          questions: questions,
        };
      });
  }, [reduxTemplates, organizationLevels]);

  // Transform Redux templates based on templateTab
  useEffect(() => {
    const filteredTemplates = allTemplatesTransformed
      .filter((template: any) => {
        // Filter by template tab (izba_provided or custom)
        return template.template_type === templateTab;
      })
      .sort((a: any, b: any) => {
        // For izba_provided templates, put 'short' category first
        if (templateTab === "izba_provided") {
          if (a.category === "short" && b.category !== "short") return -1;
          if (a.category !== "short" && b.category === "short") return 1;
        }
        return a.name.localeCompare(b.name);
      });

    setTemplates(filteredTemplates);
  }, [allTemplatesTransformed, templateTab]);

  // Auto-select accounts when Short Assessment template is selected
  // Skip this if editing (accounts should already be initialized from editingAssessment)
  useEffect(() => {
    // Don't auto-select if editing - accounts should come from editingAssessment
    if (editingAssessment) {
      return;
    }

    if (formData.template?.category === "short" && accounts.length > 0) {
      const accountsWithStakeholders = accounts.filter(account =>
        accountHasStakeholders(account, accountTeamsByAccountId[account.id])
      );

      if (accountsWithStakeholders.length > 0) {
        const accountExecutives: Record<string, string[]> = {};
        accountsWithStakeholders.forEach(account => {
          accountExecutives[account.id] = getAllDefaultStakeholderIds(
            account.id,
            accountTeamsByAccountId,
            accounts
          );
        });

        setFormData(prev => ({
          ...prev,
          target_accounts: accountsWithStakeholders.map(account => account.id),
          account_executives: accountExecutives,
        }));
      }
    } else if (
      formData.template?.category &&
      formData.template.category !== "short"
    ) {
      // Clear accounts when switching to non-short template
      setFormData(prev => ({
        ...prev,
        target_accounts: [],
        account_executives: {},
      }));
    }
  }, [
    formData.template?.category,
    accounts,
    accountTeamsByAccountId,
    editingAssessment,
  ]);

  // Handle default account selection
  // Skip this if editing (accounts should already be initialized from editingAssessment)
  useEffect(() => {
    // Don't auto-select if editing
    if (editingAssessment) {
      return;
    }

    if (
      accounts.length > 0 &&
      !formData.template &&
      formData.target_accounts.length === 0
    ) {
      const firstAccountWithExecutives = accounts.find(
        account => account.executives && account.executives.length > 0
      );

      if (firstAccountWithExecutives) {
        const executiveIds = firstAccountWithExecutives.executives.map(
          exec => exec.id
        );
        setFormData(prev => ({
          ...prev,
          target_accounts: [firstAccountWithExecutives.id],
          account_executives: {
            [firstAccountWithExecutives.id]: executiveIds,
          },
        }));
      }
    }
  }, [
    accounts,
    formData.template,
    formData.target_accounts.length,
    editingAssessment,
  ]);

  const selectTemplate = (template: any, isUserAction = true) => {
    if (isUserAction) hasUserEditedRef.current = true;
    const isChangingTemplate =
      editingAssessment &&
      formData.template_id &&
      template.id !== formData.template_id;
    if (isChangingTemplate) {
      setChangeTemplateConfirm({ isOpen: true, pendingTemplate: template });
      return;
    }
    setFormData(prev => ({
      ...prev,
      template_id: template.id,
      template: template,
      questions: template.questions || [],
    }));
  };

  const confirmChangeTemplate = () => {
    hasUserEditedRef.current = true;
    const template = changeTemplateConfirm.pendingTemplate;
    setChangeTemplateConfirm({ isOpen: false, pendingTemplate: null });
    if (!template) return;
    setFormData(prev => ({
      ...prev,
      template_id: template.id,
      template: template,
      questions: template.questions || [],
    }));
  };

  const cancelChangeTemplate = () => {
    setChangeTemplateConfirm({ isOpen: false, pendingTemplate: null });
  };

  // Auto-select template when editing - set tab first (ensure correct tab is selected)
  useEffect(() => {
    // Only run when editing and templates are loaded
    if (
      !editingAssessment?.template_id ||
      allTemplatesTransformed.length === 0 ||
      formData.template // If template object is already selected, skip
    ) {
      return;
    }

    // Check if template exists in current tab's templates
    const templateInCurrentTab = templates.find(
      t => t.id === editingAssessment.template_id
    );

    // If template is already in current tab, don't switch
    if (templateInCurrentTab) {
      return;
    }

    // Search in all templates (not filtered by tab) to find the matching template
    const matchingTemplate = allTemplatesTransformed.find(
      t => t.id === editingAssessment.template_id
    );

    if (matchingTemplate && matchingTemplate.template_type) {
      // Set tab to match template type so the template becomes available
      setTemplateTab(matchingTemplate.template_type);
    }
  }, [
    editingAssessment?.template_id,
    allTemplatesTransformed.length,
    templates.length,
    formData.template,
    templateTab,
  ]);

  // Auto-select template after tab is set
  useEffect(() => {
    if (
      editingAssessment?.template_id &&
      templates.length > 0 &&
      !formData.template
    ) {
      // Now search in filtered templates (after tab is set)
      const matchingTemplate = templates.find(
        t => t.id === editingAssessment.template_id
      );
      if (matchingTemplate) {
        selectTemplate(matchingTemplate, false); // auto-select, not user edit
      }
    }
  }, [
    editingAssessment?.template_id,
    templates.length,
    templateTab,
    formData.template,
    selectTemplate,
  ]);

  const handleTemplateTabChange = (tabId: string) => {
    hasUserEditedRef.current = true;
    setTemplateTab(tabId);
    setFormData(prev => ({
      ...prev,
      template_id: "",
      template: null,
      questions: [],
    }));
  };

  const toggleAccountSelection = (accountId: string) => {
    hasUserEditedRef.current = true;
    setFormData(prev => {
      const isCurrentlySelected = prev.target_accounts.includes(accountId);
      const isLockedAccount =
        isOngoingEdit && lockedTargetAccountIds.includes(accountId);

      if (isCurrentlySelected && isLockedAccount) {
        return prev;
      }

      if (isCurrentlySelected) {
        const newAccountExecutives = { ...prev.account_executives };
        delete newAccountExecutives[accountId];

        return {
          ...prev,
          target_accounts: prev.target_accounts.filter(id => id !== accountId),
          account_executives: newAccountExecutives,
        };
      } else {
        const executiveIds = getDefaultExecutiveIdsForAccount(accountId);

        return {
          ...prev,
          target_accounts: [...prev.target_accounts, accountId],
          account_executives: {
            ...prev.account_executives,
            [accountId]: executiveIds,
          },
        };
      }
    });
  };

  const toggleExecutiveSelection = (accountId: string, executiveId: string) => {
    hasUserEditedRef.current = true;
    setFormData(prev => {
      if (isOngoingEdit && lockedTargetAccountIds.includes(accountId)) {
        return prev;
      }
      const currentSelections = prev.account_executives[accountId] || [];
      const isSelected = currentSelections.includes(executiveId);

      return {
        ...prev,
        account_executives: {
          ...prev.account_executives,
          [accountId]: isSelected
            ? currentSelections.filter(id => id !== executiveId)
            : [...currentSelections, executiveId],
        },
      };
    });
  };

  const selectAllAccounts = () => {
    hasUserEditedRef.current = true;
    const accountsWithStakeholders = accounts.filter(account =>
      accountHasStakeholders(account, accountTeamsByAccountId[account.id])
    );

    const accountExecutives: Record<string, string[]> = {};
    accountsWithStakeholders.forEach(account => {
      accountExecutives[account.id] = getDefaultExecutiveIdsForAccount(
        account.id
      );
    });

    setFormData(prev => ({
      ...prev,
      target_accounts: accountsWithStakeholders.map(account => account.id),
      account_executives: accountExecutives,
    }));
  };

  const clearAllAccounts = () => {
    hasUserEditedRef.current = true;
    setFormData(prev => {
      if (!isOngoingEdit) {
        return {
          ...prev,
          target_accounts: [],
          account_executives: {},
        };
      }

      const lockedExecutives: Record<string, string[]> = {};
      lockedTargetAccountIds.forEach(accountId => {
        const current = prev.account_executives[accountId];
        if (current && current.length > 0) {
          lockedExecutives[accountId] = current;
          return;
        }
        lockedExecutives[accountId] =
          getDefaultExecutiveIdsForAccount(accountId);
      });

      return {
        ...prev,
        target_accounts: [...lockedTargetAccountIds],
        account_executives: lockedExecutives,
      };
    });
  };

  // Map question type to API format
  const mapQuestionTypeToAPI = (type: string): string => {
    switch (type) {
      case "textarea":
        return "free_text";
      case "dropdown":
        return "single_choice";
      case "multiple_choice":
        return "multiple_choice";
      case "scale":
        return "scale";
      default:
        return "free_text";
    }
  };

  // Build latest maturity_level choices for API (consume level_code and name as-is)
  const getMaturityLevelChoicesForAPI = useCallback((): Array<{
    label: string;
    value: string;
  }> | null => {
    if (!organizationLevels?.length) return null;
    return organizationLevels.map((level: any) => ({
      label: getAccountLevelCompactLabel(level.level_code, level.name),
      value: level.level_code,
    }));
  }, [organizationLevels]);

  // Transform questions to API format (similar to TemplateForm)
  const transformQuestionsForAPI = () => {
    const transformQuestion = (q: any, index: number, isCustom = false) => {
      const baseQuestion: any = {
        id: q.id || `q${index + 1}`,
        order_index: q.order_index || index + 1,
        question_text: q.question || q.question_text,
        question_type: mapQuestionTypeToAPI(q.type),
        required: q.required !== undefined ? q.required : true,
        is_custom: isCustom,
      };

      // For maturity_level dropdown, always send latest organization levels to backend
      const isMaturityLevel =
        q.id === "maturity_level" || q.id === "maturity level";
      const maturityChoices = isMaturityLevel
        ? getMaturityLevelChoicesForAPI()
        : null;

      // Handle options based on question type
      if (q.type === "textarea" || q.type === "free_text") {
        baseQuestion.options = {
          min_length: 1,
          max_length: 500,
        };
      } else if (
        (q.type === "dropdown" ||
          q.type === "multiple_choice" ||
          q.type === "scale") &&
        (q.options?.length > 0 || maturityChoices)
      ) {
        if (q.type === "scale" && !maturityChoices) {
          // For scale, check if options have min/max or use choices
          const hasMinMax = q.options?.some(
            (opt: any) => opt.min !== undefined || opt.max !== undefined
          );
          if (hasMinMax) {
            const minOpt = q.options.find((opt: any) => opt.min !== undefined);
            const maxOpt = q.options.find((opt: any) => opt.max !== undefined);
            baseQuestion.options = {
              min: minOpt?.min || 1,
              max: maxOpt?.max || 10,
              labels: q.options
                .filter((opt: any) => opt.label)
                .map((opt: any) => opt.label),
            };
          } else {
            baseQuestion.options = {
              choices: (q.options || [])
                .filter((opt: any) => opt.label && opt.value)
                .map((opt: any) => ({
                  label: opt.label,
                  value: opt.value,
                })),
            };
          }
        } else {
          // dropdown / multiple_choice: use latest levels for maturity_level, else q.options
          const choices =
            maturityChoices ??
            (q.options || [])
              .filter((opt: any) => opt.label && opt.value)
              .map((opt: any) => ({ label: opt.label, value: opt.value }));
          baseQuestion.options = {
            choices,
            allow_multiple: q.type === "multiple_choice",
          };
        }
      }

      return baseQuestion;
    };

    // Transform template questions
    const templateQuestions = formData.questions.map((q, idx) =>
      transformQuestion(q, idx, false)
    );

    // Transform custom questions
    const customQuestions = formData.custom_questions.map((q, idx) =>
      transformQuestion(q, templateQuestions.length + idx, true)
    );

    return [...templateQuestions, ...customQuestions];
  };

  const handleSaveDraft = async () => {
    if (isOngoingEdit) {
      return;
    }

    try {
      setLoading(true);

      const allQuestions = transformQuestionsForAPI();
      const cleanTargetAccounts = filterValidUUIDs(
        formData.target_accounts || []
      );
      const accountsPayload = cleanTargetAccounts.map(accountId => ({
        account_id: accountId,
        executive_ids: formData.account_executives[accountId] || [],
      }));

      const draftPayload = {
        name: formData.title?.trim() || "Untitled",
        description: formData.description || undefined,
        template_id: formData.template_id || "",
        accounts: accountsPayload,
        data: {
          metadata: {
            categories: [],
            template_id: formData.template_id || "",
            current_step: currentStep,
          },
          questions: allQuestions,
        },
        target_questionnaires: formData.template?.category,
        due_on: formData.due_date,
        reminder_frequency: "Daily",
        send_now: false,
      };

      let assessmentId: string;
      let updateResult: any = null;

      if (editingAssessment) {
        updateResult = await dispatch(
          updateAssessment({
            id: editingAssessment.id,
            data: draftPayload,
          })
        ).unwrap();
        assessmentId = editingAssessment.id;
        toast.success(
          updateResult?.message || "Assessment draft updated successfully!"
        );
      } else {
        const response: any = await dispatch(
          createAssessment({ ...draftPayload, status: "draft" })
        ).unwrap();

        // API responses are inconsistent across environments:
        // sometimes id is top-level, sometimes nested in data, or named assessment_id.
        const createdAssessmentId =
          response?.id ||
          response?.assessment_id ||
          response?.data?.id ||
          response?.data?.assessment_id;
        if (!createdAssessmentId) {
          throw new Error("Failed to resolve created assessment id");
        }
        assessmentId = createdAssessmentId;
        toast.success("Assessment saved as draft successfully!");
      }

      // Clear "user edited" so closing without further edits won't show confirm modal
      hasUserEditedRef.current = false;

      onComplete?.({
        ...formData,
        id: assessmentId,
        status: "draft",
        assignmentsCreated: 0,
        navigateToTab: "pending",
      });
    } catch (error: any) {
      console.error("Error saving assessment draft:", error);
      toast.error(
        error?.message || "Error saving assessment draft. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraftAndClose = useCallback(async () => {
    try {
      await handleSaveDraft();
      setShowDiscardConfirm(false);
      onClose();
    } catch {
      // Error already handled in handleSaveDraft
    }
  }, [onClose, handleSaveDraft]);

  const handleLaunchAssessment = async () => {
    try {
      setLoading(true);

      if (!formData.title.trim()) {
        alert("Assessment title is required");
        return;
      }

      if (formData.title.length > 100) {
        alert("Assessment title must be 100 characters or less");
        return;
      }

      if (formData.description.length > 500) {
        alert("Assessment description must be 500 characters or less");
        return;
      }

      if (!formData.template_id) {
        alert("Please select a template");
        return;
      }

      if (formData.target_accounts.length === 0) {
        alert("Please select at least one account");
        return;
      }

      if (!formData.due_date) {
        alert("Please set a due date");
        return;
      }

      // Validate custom questions
      const invalidCustomQuestions = formData.custom_questions.some(
        q =>
          !q.question.trim() ||
          ((q.type === "dropdown" ||
            q.type === "multiple_choice" ||
            q.type === "scale") &&
            (!q.options ||
              q.options.length === 0 ||
              q.options.some((opt: any) => !opt.label || !opt.value)))
      );

      if (invalidCustomQuestions) {
        alert(
          "Please fill all custom question fields and ensure all options have both label and value"
        );
        return;
      }

      const allQuestions = transformQuestionsForAPI();

      const cleanTargetAccounts = filterValidUUIDs(
        formData.target_accounts || []
      );

      // Build accounts array with account_id and executive_ids
      const accountsPayload = cleanTargetAccounts.map(accountId => {
        const executiveIds = formData.account_executives[accountId] || [];
        return {
          account_id: accountId,
          executive_ids: executiveIds,
        };
      });

      if (editingAssessment) {
        // Update existing assessment and launch it - send all data same as create
        const updatePayload = {
          name: formData.title,
          description: formData.description || undefined,
          template_id: formData.template_id,
          accounts: accountsPayload,
          data: {
            metadata: {
              categories: [],
              template_id: formData.template_id,
            },
            questions: allQuestions,
          },
          target_questionnaires: formData.template?.category,
          due_on: formData.due_date,
          reminder_frequency: "Daily",
          send_now: true,
        };

        const updateResult: any = await dispatch(
          updateAssessment({
            id: editingAssessment.id,
            data: updatePayload,
          })
        ).unwrap();

        // Launch the assessment by updating status to active
        await dispatch(
          updateAssessmentStatus({
            id: editingAssessment.id,
            status: "active",
          })
        ).unwrap();

        toast.success(
          updateResult?.message ||
            "Assessment published and launched successfully!"
        );

        if (onComplete) {
          onComplete({
            ...formData,
            id: editingAssessment.id,
            assignmentsCreated: formData.target_accounts.length,
            navigateToTab: "ongoing",
            status: "active",
          });
        }

        onClose();
        return;
      }

      const requestData = {
        name: formData.title,
        description: formData.description || undefined,
        template_id: formData.template_id,
        accounts: accountsPayload,
        data: {
          metadata: {
            categories: [],
            template_id: formData.template_id,
          },
          questions: allQuestions,
        },
        target_questionnaires: formData.template?.category,
        status: "active" as const,
        due_on: formData.due_date,
        reminder_frequency: "Daily",
        send_now: true,
      };

      const assessmentResponse = await dispatch(
        createAssessment(requestData)
      ).unwrap();

      // Parent (AssessmentsContainer) shows the success toast for new
      // assessments via onComplete — only the edit/launch-draft path above
      // shows its own toast here, since the parent skips it when editing.

      if (onComplete) {
        onComplete({
          ...formData,
          id: assessmentResponse.id,
          assignmentsCreated: cleanTargetAccounts.length,
          navigateToTab: "ongoing",
          status: "active",
        });
      }

      onClose();
    } catch (error) {
      console.error("Error creating assessment:", error);
      const rejection = error as CreateAssessmentRejection | undefined;
      if (rejection?.code === "FORMAL_RUNNING" && rejection.accounts?.length) {
        setFormalRunningConflict(rejection.accounts);
      } else {
        alert(
          rejection?.message || "Error creating assessment. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const nextStep = async () => {
    if (currentStep >= steps.length) return;

    // Account Targeting -> Review & Launch: Formal-running blocks.
    if (currentStep === 4 && formData.target_accounts.length > 0) {
      setIsCheckingLaunchConflicts(true);
      try {
        const formalBlocking = await dispatch(
          checkFormalRunning({
            account_ids: formData.target_accounts,
            ...(editingAssessment?.id
              ? { exclude_assessment_id: editingAssessment.id }
              : {}),
          })
        ).unwrap();
        if (formalBlocking?.length) {
          setFormalRunningConflict(formalBlocking);
          return;
        }
      } catch (err) {
        console.error("Error checking launch conflicts:", err);
        // Fail open — don't block the wizard on a check-only endpoint error.
      } finally {
        setIsCheckingLaunchConflicts(false);
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (isOngoingEdit) {
      return;
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1TemplateSelection
            templateTab={templateTab}
            templates={templates}
            loading={templatesLoading}
            selectedTemplateId={formData.template_id}
            onTemplateTabChange={handleTemplateTabChange}
            onSelectTemplate={selectTemplate}
          />
        );
      case 2:
        return (
          <Step2QuestionPreview
            template={formData.template}
            questions={formData.questions}
            customQuestions={formData.custom_questions}
            organizationLevels={organizationLevels || []}
            onUpdateCustomQuestions={questions => {
              hasUserEditedRef.current = true;
              setFormData(prev => ({ ...prev, custom_questions: questions }));
            }}
          />
        );
      case 4:
        return (
          <Step3AccountTargeting
            accounts={accounts}
            selectedAccountIds={formData.target_accounts}
            accountExecutives={formData.account_executives}
            accountTeamsByAccountId={accountTeamsByAccountId}
            teamsLoading={isLoadingAccountTeams}
            lockedAccountIds={lockedTargetAccountIds}
            isOngoingEdit={isOngoingEdit}
            onToggleAccount={toggleAccountSelection}
            onToggleExecutive={toggleExecutiveSelection}
            onSelectAll={selectAllAccounts}
            onClearAll={clearAllAccounts}
          />
        );
      case 3:
        return (
          <Step4Configuration
            title={formData.title}
            description={formData.description}
            dueDate={formData.due_date}
            onUpdateTitle={title => {
              hasUserEditedRef.current = true;
              setFormData(prev => ({ ...prev, title }));
            }}
            onUpdateDescription={description => {
              hasUserEditedRef.current = true;
              setFormData(prev => ({ ...prev, description }));
            }}
            onUpdateDueDate={dueDate => {
              hasUserEditedRef.current = true;
              setFormData(prev => ({ ...prev, due_date: dueDate }));
            }}
          />
        );
      case 5:
        return (
          <Step5ReviewLaunch
            title={formData.title}
            templateName={formData.template?.name || ""}
            dueDate={formData.due_date}
            selectedAccounts={accounts.filter(acc =>
              formData.target_accounts.includes(acc.id)
            )}
            selectedAccountIds={formData.target_accounts}
            existingAccountIds={lockedTargetAccountIds}
            accountExecutives={formData.account_executives}
            accountTeamsByAccountId={accountTeamsByAccountId}
            isOngoingEdit={isOngoingEdit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex p-4">
      <div className="relative m-auto p-6 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingAssessment ? "Edit Assessment" : "Create New Assessment"}
          </h2>
          <Button onClick={handleCloseRequest} variant="clear">
            <X className="h-6 w-6" />
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div
                  key={step.id}
                  className="flex flex-col items-center flex-shrink-0"
                >
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 flex-shrink-0 ${
                      isCompleted
                        ? "bg-purple-600 border-purple-600 text-white"
                        : isActive
                          ? "border-purple-600 text-purple-600 bg-white"
                          : "border-gray-300 text-gray-400"
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <p
                      className={`text-sm font-medium ${
                        isActive
                          ? "text-purple-600"
                          : isCompleted
                            ? "text-gray-900"
                            : "text-gray-500"
                      }`}
                    >
                      {step.name}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-4 ${
                        currentStep > step.id ? "bg-purple-600" : "bg-gray-300"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="mb-6 min-h-96">{renderStepContent()}</div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 1 || isOngoingEdit}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </button>

          <div className="text-sm text-gray-500">
            Step {currentStep} of {steps.length}
          </div>

          {currentStep === steps.length ? (
            <div className="flex space-x-3">
              <button
                onClick={handleSaveDraft}
                disabled={loading || isOngoingEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Save as Draft (Pending)
                  </>
                )}
              </button>
              <button
                onClick={handleLaunchAssessment}
                disabled={
                  loading ||
                  !formData.title.trim() ||
                  !formData.due_date ||
                  formData.target_accounts.length === 0
                }
                className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Publishing...
                  </>
                ) : (
                  <>
                    <Rocket className="h-4 w-4 mr-2" />
                    {isOngoingEdit
                      ? "Save Accounts"
                      : "Publish & Launch (Ongoing)"}
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="flex space-x-3">
              {currentStep >= 3 && !isOngoingEdit && (
                <button
                  onClick={handleSaveDraft}
                  disabled={
                    loading || !formData.title?.trim() || !formData.due_date
                  }
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      Save as Draft (Pending)
                    </>
                  )}
                </button>
              )}
              <button
                onClick={nextStep}
                disabled={
                  isCheckingLaunchConflicts ||
                  (currentStep === 1 && !formData.template_id) ||
                  (currentStep === 4 &&
                    formData.target_accounts.length === 0) ||
                  (currentStep === 3 && (!formData.title || !formData.due_date))
                }
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCheckingLaunchConflicts ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Checking...
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showDiscardConfirm}
        toggle={() => setShowDiscardConfirm(false)}
        title="Discard changes?"
        size="md"
      >
        <p className="text-gray-700 mb-4">
          Some changes will not be saved. Are you sure you want to leave?
        </p>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline-primary"
            onClick={() => setShowDiscardConfirm(false)}
            disabled={loading}
          >
            Stay
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveDraftAndClose}
            loading={loading}
            disabled={
              loading ||
              isOngoingEdit ||
              !formData.title?.trim() ||
              !formData.due_date
            }
            icon={<FileText size={16} />}
          >
            Save as Draft
          </Button>
          <Button variant="outline-primary" onClick={handleDiscardAndClose}>
            Leave
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={changeTemplateConfirm.isOpen}
        toggle={cancelChangeTemplate}
        title="Change template?"
        size="md"
      >
        <p className="text-gray-700 mb-4">
          Are you sure you want to change the template? This may affect
          questions and configuration for this assessment.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline-primary" onClick={cancelChangeTemplate}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmChangeTemplate}>
            Yes, change template
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={!!formalRunningConflict}
        toggle={() => setFormalRunningConflict(null)}
        title="Formal assessment already running"
        size="md"
      >
        <p className="text-gray-700 mb-4">
          {formalRunningConflict?.length === 1
            ? "A formal assessment is already in progress on this account."
            : `A formal assessment is already in progress on ${formalRunningConflict?.length} of the accounts you selected.`}{" "}
          Remove the account, or wait until that assessment is published.
        </p>
        <ul className="mb-4 max-h-40 overflow-y-auto text-sm text-gray-600">
          {formalRunningConflict?.map(a => (
            <li key={a.account_id} className="py-1">
              <span className="font-medium text-gray-800">
                {accounts.find(acc => acc.id === a.account_id)?.name ||
                  "Unknown account"}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={() => setFormalRunningConflict(null)}
          >
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AssessmentCreationWizard;
